import { useState } from 'react';
import { createCertificate, updateCertificate } from '../../services/content/certificateService';
import { useAssociationLookup } from '../../hooks/useAssociationLookup';
import type { Certificate } from '../../types/content/certificate';

interface CertificateModalProps {
  certificate?: Certificate;
  onClose: () => void;
  onSuccess: () => void;
}

export function CertificateModal({ certificate, onClose, onSuccess }: CertificateModalProps) {
  const isEdit = !!certificate;
  const { getOptionsForAssociation } = useAssociationLookup();

  const [name, setName] = useState(certificate?.name || '');
  const [description, setDescription] = useState(certificate?.description || '');
  const [template, setTemplate] = useState(certificate?.template || '');
  const [associationId, setAssociationId] = useState(
    certificate?.association === 'certification' ? (certificate?.associationId || '') : ''
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  async function handleSave() {
    if (!name.trim() || !associationId.trim()) return;
    setError('');
    setLoading(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || null,
        template: template.trim() || null,
        association: 'certification' as const,
        associationId: associationId.trim(),
      };
      if (isEdit) {
        await updateCertificate(certificate!.id, { ...certificate!, ...data });
      } else {
        await createCertificate(data);
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update certificate' : 'Failed to create certificate');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {isEdit ? 'Edit Certificate' : 'Add Certificate'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Certificate name" />
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} rows={3} placeholder="Certificate description" />
            </div>
            <div>
              <label className={labelClass}>Template</label>
              <textarea value={template} onChange={(e) => setTemplate(e.target.value)} className={inputClass} rows={4} placeholder="Certificate HTML template" />
            </div>
            {(() => {
              const options = getOptionsForAssociation('certification');
              return options.length > 0 ? (
                <div>
                  <label className={labelClass}>Certification *</label>
                  <select value={associationId} onChange={(e) => setAssociationId(e.target.value)} className={inputClass}>
                    <option value="">Select...</option>
                    {options.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className={labelClass}>Certification ID *</label>
                  <input type="text" value={associationId} onChange={(e) => setAssociationId(e.target.value)} className={inputClass} placeholder="UUID of certification" />
                </div>
              );
            })()}
          </div>

          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={loading || !name.trim() || !associationId.trim()} className={btnPrimary}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
